import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
const SUP_API = `${BASE}/api/supervisor`;

export const supHttp = axios.create({ baseURL: SUP_API });

supHttp.interceptors.request.use((config) => {
  try {
    const t = localStorage.getItem("supervisor_token");
    if (t) config.headers.Authorization = `Bearer ${t}`;
  } catch {
    /* ignore */
  }
  return config;
});

supHttp.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      try {
        localStorage.removeItem("supervisor_token");
        localStorage.removeItem("supervisor_user");
      } catch {
        /* ignore */
      }
    }
    return Promise.reject(err);
  }
);

export const supervisorApi = {
  login: (username, password, totp_code) =>
    supHttp.post("/login", { username, password, totp_code }),
  verify2fa: (challenge_token, code) =>
    supHttp.post("/2fa/verify", { challenge_token, code }),
  setup2fa: () => supHttp.post("/2fa/setup"),
  enable2fa: (code) => supHttp.post("/2fa/enable", { code }),
  disable2fa: (password, code) =>
    supHttp.post("/2fa/disable", { password, code }),
  me: () => supHttp.get("/me"),
  changePassword: (old_password, new_password) =>
    supHttp.post("/change-password", { old_password, new_password }),
  listTenants: () => supHttp.get("/tenants"),
  getTenant: (code) => supHttp.get(`/tenants/${code}`),
  createTenant: (payload) => supHttp.post("/tenants", payload),
  updateTenant: (code, payload) => supHttp.patch(`/tenants/${code}`, payload),
  suspendTenant: (code, suspended, reason) =>
    supHttp.post(`/tenants/${code}/suspend`, { suspended, reason }),
  deleteTenant: (code, password) =>
    supHttp.delete(`/tenants/${code}`, {
      headers: { "X-Supervisor-Password": password || "" },
    }),
  resetPin: (code, new_pin) =>
    supHttp.post(`/tenants/${code}/reset-pin`, { new_pin }),
  extendSubscription: (code, days) =>
    supHttp.post(`/tenants/${code}/extend-subscription`, { days }),
  clearLockouts: (code) =>
    supHttp.post(`/tenants/${code}/clear-lockouts`),
  forceLogout: (code) =>
    supHttp.post(`/tenants/${code}/force-logout`),
  generateDeviceCode: (code, label_hint) =>
    supHttp.post(`/tenants/${code}/devices/generate-code`, { label_hint: label_hint || null }),
  resetDeviceGrace: (code) =>
    supHttp.post(`/tenants/${code}/devices/reset-grace`),
  getDeviceTrust: (code) =>
    supHttp.get(`/tenants/${code}/device-trust`),
  setDeviceTrust: (code, enabled) =>
    supHttp.patch(`/tenants/${code}/device-trust`, { enabled }),
  impersonate: (code) => supHttp.post(`/tenants/${code}/impersonate`),
  // Feb 2026: tenant export / import (backup migration between tenants).
  exportTenant: (code) =>
    supHttp.get(`/tenants/${code}/export`, { responseType: "blob" }),
  importTenant: (code, file, password, collections) => {
    const fd = new FormData();
    fd.append("file", file);
    // Selective import — omit the field for a full restore (legacy).
    if (Array.isArray(collections)) {
      fd.append("collections", JSON.stringify(collections));
    }
    return supHttp.post(`/tenants/${code}/import`, fd, {
      headers: {
        "X-Supervisor-Password": password || "",
        "Content-Type": "multipart/form-data",
      },
    });
  },
  stats: () => supHttp.get("/stats"),
  audit: (limit = 200) => supHttp.get(`/audit?limit=${limit}`),
  plans: () => supHttp.get("/plans"),
  // Feb 2026: Supervisor Test Runner
  listTests: () => supHttp.get("/tests/list"),
  runTests: (files) => supHttp.post("/tests/run", { files: files || [] }),
  listRuns: () => supHttp.get("/tests/runs"),
  getRun: (runId) => supHttp.get(`/tests/runs/${runId}`),
};
